import * as React from 'react';
import { ArrowUpRight, Sparkles, UserRound } from 'lucide-react';
import { Link } from 'wouter';

import { Button } from '@/components/ui/button';
import { normalizeImageUrl } from '@/lib/image';
import { trackPublicFunnelEvent } from '@/lib/public-analytics';
import { cn } from '@/lib/utils';

type CreatorCardVariant = 'compact' | 'comfortable' | 'expanded';

interface CreatorCardProps {
  id: string;
  displayName: string;
  creatorHeadline: string;
  bio?: string | null;
  profileImageUrl?: string | null;
  experience?: string | null;
  isVerified?: boolean;
  localityName?: string | null;
  detailHref?: string;
  variant?: CreatorCardVariant;
  className?: string;
}

const variantMap: Record<
  CreatorCardVariant,
  {
    wrapper: string;
    image: string;
    name: string;
    description: string;
    buttonLabel: string;
  }
> = {
  compact: {
    wrapper: 'rounded-[1.25rem] border border-border/70 p-4 shadow-sm',
    image: 'h-16 w-16',
    name: 'text-lg',
    description: 'line-clamp-2 text-sm',
    buttonLabel: 'View',
  },
  comfortable: {
    wrapper: 'rounded-[1.5rem] border border-border/70 p-5 shadow-sm',
    image: 'h-20 w-20',
    name: 'text-xl',
    description: 'line-clamp-3 text-sm',
    buttonLabel: 'Meet creator',
  },
  expanded: {
    wrapper: 'rounded-[1.75rem] border border-border/70 p-6 shadow-md',
    image: 'h-24 w-24',
    name: 'text-2xl',
    description: 'line-clamp-3 text-base',
    buttonLabel: 'See profile',
  },
};

export default function CreatorCard({
  id,
  displayName,
  creatorHeadline,
  bio,
  profileImageUrl,
  experience,
  isVerified,
  localityName,
  detailHref,
  variant = 'comfortable',
  className,
}: CreatorCardProps) {
  const styles = variantMap[variant];
  const href = detailHref ?? `/creators/${id}`;
  const [imageFailed, setImageFailed] = React.useState(false);
  const creatorImageUrl =
    profileImageUrl && !imageFailed ? normalizeImageUrl(profileImageUrl, '') : null;

  React.useEffect(() => {
    setImageFailed(false);
  }, [profileImageUrl]);

  const handleDetailClick = () => {
    void trackPublicFunnelEvent({
      eventName: 'discovery_to_detail',
      source: 'creator_card',
      destinationType: 'creator_detail',
      creatorId: id,
    });
  };

  return (
    <article
      className={cn(
        'flex h-full flex-col bg-card transition-transform duration-200 hover:-translate-y-1',
        styles.wrapper,
        className
      )}
      data-testid={`creator-card-${id}`}
    >
      <div className="flex items-start gap-4">
        <Link href={href} onClick={handleDetailClick} className="relative shrink-0">
          {creatorImageUrl ? (
            <img
              src={creatorImageUrl}
              alt={displayName}
              className={cn('rounded-full object-cover ring-1 ring-border/60', styles.image)}
              data-testid={`creator-avatar-${id}`}
              onError={() => setImageFailed(true)}
            />
          ) : (
            <div
              className={cn(
                'flex items-center justify-center rounded-full bg-muted text-muted-foreground ring-1 ring-border/60',
                styles.image
              )}
              data-testid={`creator-avatar-placeholder-${id}`}
              aria-label={`${displayName} has not uploaded a profile photo`}
            >
              <UserRound className="h-1/2 w-1/2" />
            </div>
          )}
          {isVerified ? (
            <span className="absolute -right-1 -top-1 rounded-full bg-primary p-1 text-primary-foreground">
              <Sparkles className="h-3 w-3" />
            </span>
          ) : null}
        </Link>

        <div className="min-w-0 flex-1">
          <Link href={href} onClick={handleDetailClick}>
            <h3
              className={cn(
                'truncate font-semibold text-foreground transition-colors hover:text-primary',
                styles.name
              )}
              data-testid={`creator-name-${id}`}
            >
              {displayName}
            </h3>
          </Link>
          <p className="mt-1 text-sm font-medium text-primary">{creatorHeadline}</p>
          {localityName ? (
            <p className="mt-1 text-xs uppercase tracking-[0.16em] text-muted-foreground">
              {localityName}
            </p>
          ) : null}
        </div>
      </div>

      <p className={cn('mt-4 flex-1 leading-6 text-muted-foreground', styles.description)}>
        {bio ||
          experience ||
          `${creatorHeadline} sharing practical, neighborhood-rooted creative knowledge.`}
      </p>

      <div className="mt-5">
        <Link href={href} onClick={handleDetailClick}>
          <Button variant="outline" className="touch-target rounded-full px-4">
            {styles.buttonLabel}
            <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </article>
  );
}
